import React, { useEffect, useState } from 'react';

import { styled } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Container from '@material-ui/core/Container';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';

import DescriptionIcon from '@material-ui/icons/Description';
import AddTaskIcon from '@material-ui/icons/AddTask';

import { useAPI } from '../../utils/api';
import { makeStyles } from '@material-ui/styles';
import CombineSelector from '../../components/combineSelector';
import { MODE } from '../../utils/mode';

const useStyles = makeStyles({
  root: {
    '&:last-of-type td': {
      borderBottom: 'none',
    },
  },
});

const MemberItemContainer = styled(Grid)({
  textAlign: 'center',
  borderLeft: '1px solid',
});

const MemberItemGrid = styled(Grid)({
  padding: 6,
  borderBottom: '1px solid',
  borderRight: '1px solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflowWrap: 'anywhere',
});

const createData = ({
  memberID,
  accountCode,
  abbreviation,
  mandarinName,
  name,
  paymentType,
}) => {
  const detailUrl = `/#/member/${MODE.VIEW}/${memberID}`;
  return {
    memberID,
    abbreviation,
    mandarinName,
    accountCode,
    name,
    paymentType,
    detailUrl,
  };
};

const createOrder = (memberID) => {
  window.open(`#/order/${MODE.NEW}/${memberID}`, '_blank');
};

const OrderListPage = () => {
  const classes = useStyles();
  const api = useAPI();

  const [cityList, setCityList] = useState([]);
  const [members, setMembers] = useState([]);
  const [isUnmounted, setIsUnmounted] = useState(false);
  const [searchFormEmpty, setSearchFormEmpty] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const columns = [
    { id: 'abbreviation', label: '會員簡稱' },
    { id: 'mandarinName', label: '中文名稱' },
    {
      id: 'accountCode',
      label: '會員編號',
    },
    {
      id: 'name',
      label: '姓名',
    },
    {
      id: 'paymentType',
      label: '付款方式',
    },
    {
      id: 'function',
      label: '功能',
      minWidth: '240px',
    },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const fetchOrders = (data) => {
    api
      .getOrderList(data)
      .then((result) => {
        const rows = [];
        for (const member of result) {
          rows.push(createData(member));
        }

        setMembers(rows);
      })
      .catch(() => {});
  };

  const searchMembers = (event) => {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);
    const data = {
      memberID: -1,
      accountCode: formData.get('accountCode') || '',
      abbreviation: formData.get('abbreviation') || '',
      districtID: Number.parseInt(formData.get('districtID')) || 0,
      address: formData.get('address') || '',
      phone: formData.get('phone') || '',
    };

    fetchOrders(data);
  };

  useEffect(() => {
    api
      .getDropdownCitys()
      .then((result) => {
        if (!isUnmounted) {
          setCityList(result);
          fetchOrders();
        }
      })
      .catch(() => {});

    return () => {
      setIsUnmounted(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearCondition = () => {
    setSearchFormEmpty(true);
  };

  useEffect(() => {
    setSearchFormEmpty(false);
  }, [searchFormEmpty]);

  return (
    <>
      <Paper
        elevation={4}
        sx={{
          paddingTop: { xs: 1, md: 2 },
          paddingBottom: { xs: 2, md: 4 },
          marginBottom: 4,
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <Container>
          <Typography variant="h6" gutterBottom>
            查詢條件
          </Typography>
          <Box component="form" noValidate onSubmit={searchMembers}>
            <Grid container spacing={{ xs: 1, md: 2 }}>
              <Grid item xs={12} sm={6}>
                {!searchFormEmpty ? (
                  <TextField
                    required
                    name="accountCode"
                    label="會員編號"
                    fullWidth
                    variant="standard"
                    size="small"
                  />
                ) : undefined}
              </Grid>
              <Grid item xs={12} sm={6}>
                {!searchFormEmpty ? (
                  <TextField
                    required
                    name="abbreviation"
                    label="會員簡稱"
                    fullWidth
                    variant="standard"
                    size="small"
                  />
                ) : undefined}
              </Grid>
              {!searchFormEmpty ? (
                <CombineSelector
                  sx={{ marginTop: 1 }}
                  xs={6}
                  sm={3}
                  size="small"
                  isVariable={false}
                  optionsList={{
                    firstName: 'city',
                    firstLabel: '縣市',
                    options: cityList,
                    secondName: 'districtID',
                    secondLabel: '區域',
                  }}
                  secondOptionsKeyName="districts"
                  variant="outlined"
                />
              ) : undefined}
              <Grid item xs={12} sm={6}>
                {!searchFormEmpty ? (
                  <TextField
                    required
                    name="address"
                    label="地址"
                    fullWidth
                    variant="standard"
                    size="small"
                    autoComplete="none"
                  />
                ) : undefined}
              </Grid>
              <Grid item xs={12}>
                {!searchFormEmpty ? (
                  <TextField
                    required
                    name="phone"
                    label="電話"
                    fullWidth
                    variant="standard"
                    size="small"
                    autoComplete="none"
                  />
                ) : undefined}
              </Grid>
              <Grid
                container
                spacing={3}
                sx={{ margin: 'auto', width: '100%', justifyContent: 'center' }}
              >
                <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                  >
                    搜尋會員
                  </Button>
                </Grid>
                <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                  <Button
                    type="button"
                    fullWidth
                    variant="contained"
                    color="error"
                    onClick={() => clearCondition()}
                  >
                    清除
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Paper>

      <Typography variant="body2" marginBottom={1} textAlign="center">
        共找到 {members ? members.length : 0} 筆資料
      </Typography>

      <Paper
        sx={{ width: '100%', overflow: 'hidden', padding: 1 }}
        elevation={5}
      >
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow
                sx={{
                  display: { xs: 'none', sm: 'table-row' },
                }}
              >
                {members && columns
                  ? columns.map((column, index) => (
                      <TableCell
                        key={`${column.id}-${index}`}
                        align={column.align || 'center'}
                        sx={{
                          minWidth: column.minWidth || 'none',
                          padding: 1,
                          maxWidth: '100%',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))
                  : undefined}
              </TableRow>
            </TableHead>

            <TableBody>
              {members
                ? members
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={`${row.memberID}-${index}`}
                          className={classes.root}
                        >
                          {members && columns
                            ? columns.map((column, index) => {
                                return (
                                  <TableCell
                                    key={`${column.id}-${index}`}
                                    align={column.align || 'center'}
                                    sx={{
                                      display: { xs: 'none', sm: 'table-cell' },
                                      padding: 1,
                                      minHeight: '0',
                                      maxWidth: '100%',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {column.id === 'function' ? (
                                      <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                          <Link
                                            underline="none"
                                            href={row.detailUrl}
                                          >
                                            <Button
                                              type="button"
                                              fullWidth
                                              variant="contained"
                                              color="info"
                                              startIcon={<DescriptionIcon />}
                                              sx={{ color: 'white' }}
                                            >
                                              會員資料
                                            </Button>
                                          </Link>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <Button
                                            type="button"
                                            fullWidth
                                            variant="contained"
                                            color="success"
                                            sx={{ color: 'white' }}
                                            startIcon={<AddTaskIcon />}
                                            onClick={() =>
                                              createOrder(row.memberID)
                                            }
                                          >
                                            建立訂單
                                          </Button>
                                        </Grid>
                                      </Grid>
                                    ) : (
                                      row[column.id]
                                    )}
                                  </TableCell>
                                );
                              })
                            : undefined}

                          <TableCell
                            sx={{
                              display: {
                                xs: 'block',
                                sm: 'none',
                                width: '100%',
                              },
                            }}
                          >
                            <MemberItemContainer
                              container
                              borderTop="1px solid"
                              fontWeight="bold"
                            >
                              <MemberItemGrid item xs={4}>
                                會員簡稱
                              </MemberItemGrid>
                              <MemberItemGrid item xs={8}>
                                中文名稱
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={4}>
                                {row.abbreviation}
                              </MemberItemGrid>
                              <MemberItemGrid item xs={8}>
                                {row.mandarinName}
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={4}>
                                會員編號
                              </MemberItemGrid>
                              <MemberItemGrid item xs={8}>
                                姓名
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={4}>
                                {row.accountCode}
                              </MemberItemGrid>
                              <MemberItemGrid item xs={8}>
                                {row.name}
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container fontWeight="bold">
                              <MemberItemGrid item xs={4}>
                                付款方式
                              </MemberItemGrid>
                              <MemberItemGrid item xs={8}>
                                功能
                              </MemberItemGrid>
                            </MemberItemContainer>
                            <MemberItemContainer container>
                              <MemberItemGrid item xs={4}>
                                {row.paymentType}
                              </MemberItemGrid>
                              <Grid
                                item
                                container
                                xs={8}
                                spacing={1}
                                padding={1}
                                borderBottom="1px solid"
                                borderRight="1px solid"
                                justifyContent="center"
                              >
                                <Grid item xs={12}>
                                  <Link underline="none" href={row.detailUrl}>
                                    <Button
                                      type="button"
                                      fullWidth
                                      variant="contained"
                                      color="info"
                                      startIcon={<DescriptionIcon />}
                                      sx={{ color: 'white', maxWidth: '180px' }}
                                      size="small"
                                    >
                                      會員資料
                                    </Button>
                                  </Link>
                                </Grid>
                                <Grid item xs={12}>
                                  <Button
                                    type="button"
                                    fullWidth
                                    variant="contained"
                                    color="success"
                                    startIcon={<AddTaskIcon />}
                                    onClick={() => createOrder(row.memberID)}
                                    sx={{ color: 'white', maxWidth: '180px' }}
                                    size="small"
                                  >
                                    建立訂單
                                  </Button>
                                </Grid>
                              </Grid>
                            </MemberItemContainer>
                          </TableCell>
                        </TableRow>
                      );
                    })
                : undefined}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          labelRowsPerPage="每頁顯示"
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={members ? members.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
};

export default OrderListPage;
